import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { PLAN_LABELS, formatPrice, formatExpiry } from '@/lib/plans';

interface SubscriptionRecord {
  id: string;
  plan: string;
  price: number;
  status: string;
  created_at: string;
  expires_at: string | null;
  activated_at: string | null;
}

interface RefundRecord {
  id: string;
  plan: string;
  paid_price: number;
  refund_amount: number;
  status: string;
  reason: string | null;
  created_at: string;
  reviewed_at: string | null;
}

interface Props {
  userId: string;
}

export default function SubscriptionHistory({ userId }: Props) {
  const [subs, setSubs] = useState<SubscriptionRecord[]>([]);
  const [refunds, setRefunds] = useState<RefundRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, refundRes] = await Promise.all([
        supabase
          .from('subscriptions')
          .select('id, plan, price, status, created_at, expires_at, activated_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
        supabase
          .from('refund_requests')
          .select('id, plan, paid_price, refund_amount, status, reason, created_at, reviewed_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
      ]);

      if (subRes.error) throw subRes.error;
      if (refundRes.error) throw refundRes.error;

      setSubs((subRes.data as SubscriptionRecord[]) || []);
      setRefunds((refundRes.data as RefundRecord[]) || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
            <i className="ri-check-line"></i> 이용 중
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
            <i className="ri-time-line"></i> 승인 대기
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-600 text-xs font-semibold">
            <i className="ri-close-line"></i> 거절
          </span>
        );
      case 'refunded':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold">
            <i className="ri-arrow-go-back-line"></i> 환불 완료
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-semibold">
            {status}
          </span>
        );
    }
  };

  const refundStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
            <i className="ri-time-line"></i> 환불 검토 중
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
            <i className="ri-check-line"></i> 환불 승인
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-600 text-xs font-semibold">
            <i className="ri-close-line"></i> 환불 거절
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-semibold">
            {status}
          </span>
        );
    }
  };

  const dateStr = (d: string) =>
    new Date(d).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const hasData = subs.length > 0 || refunds.length > 0;

  return (
    <div className="bg-white rounded-xl border border-background-200 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-file-list-3-line text-xl"></i>
        </div>
        <div>
          <h3 className="font-semibold text-foreground-950">이용권 구매내역</h3>
          <p className="text-sm text-foreground-500">이용권 구매·연장·환불 내역을 확인합니다.</p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      )}

      {!loading && !hasData && (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
            <i className="ri-inbox-line text-xl"></i>
          </div>
          <p className="text-sm text-foreground-500">구매 내역이 없습니다.</p>
        </div>
      )}

      {!loading && hasData && (
        <div className="space-y-4">
          {/* 구독 내역 */}
          {subs.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <i className="ri-shopping-bag-line text-primary-600"></i>
                이용권 구매 / 연장
              </h4>
              <div className="divide-y divide-background-100 border border-background-200 rounded-lg overflow-hidden">
                {subs.map((item) => (
                  <div
                    key={item.id}
                    className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground-900 text-sm">
                          {PLAN_LABELS[item.plan as keyof typeof PLAN_LABELS] || item.plan}
                        </span>
                        {statusBadge(item.status)}
                      </div>
                      <div className="text-xs text-foreground-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>신청일: {dateStr(item.created_at)}</span>
                        {item.activated_at && (
                          <span>활성일: {dateStr(item.activated_at)}</span>
                        )}
                        {item.expires_at && (
                          <span>만료일: {formatExpiry(item.expires_at)}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                      {formatPrice(item.price)}원
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 환불 내역 */}
          {refunds.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <i className="ri-arrow-go-back-line text-secondary-600"></i>
                환불 신청
              </h4>
              <div className="divide-y divide-background-100 border border-background-200 rounded-lg overflow-hidden">
                {refunds.map((item) => (
                  <div
                    key={item.id}
                    className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground-900 text-sm">
                          {PLAN_LABELS[item.plan as keyof typeof PLAN_LABELS] || item.plan}
                        </span>
                        {refundStatusBadge(item.status)}
                      </div>
                      <div className="text-xs text-foreground-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>신청일: {dateStr(item.created_at)}</span>
                        {item.reviewed_at && (
                          <span>처리일: {dateStr(item.reviewed_at)}</span>
                        )}
                        {item.reason && <span>사유: {item.reason}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-foreground-500 line-through">
                        {formatPrice(item.paid_price)}원
                      </div>
                      <div className="text-sm font-semibold text-emerald-700">
                        환불 {formatPrice(item.refund_amount)}원
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}